import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtUSD, fmtDate, relDays } from "../lib/format";
import { Overline, Monogram, ScoreBar, RiskBadge, Tag, EmptyState } from "../components/shared";
import { useCompare } from "../context/CompareContext";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Search, SlidersHorizontal, X, ArrowUpDown, Columns3, Save, Check, GitCompareArrows, Loader2, Table2, ChevronDown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const ALL_COLUMNS = [
  { key: "score", label: "Score", always: false },
  { key: "category", label: "Category" },
  { key: "sectors", label: "Sectors" },
  { key: "chains", label: "Chain" },
  { key: "stage", label: "Stage" },
  { key: "jurisdiction", label: "Jurisdiction" },
  { key: "fdv", label: "FDV" },
  { key: "funds_raised", label: "Raised" },
  { key: "tge_date", label: "TGE" },
  { key: "decentralization", label: "Decentralization" },
  { key: "team_visibility", label: "Team" },
  { key: "risk_level", label: "Risk" },
];
const DEFAULT_COLS = ["score", "category", "chains", "stage", "jurisdiction", "fdv", "funds_raised", "tge_date", "risk_level"];

function FilterSelect({ label, value, options, onChange, testId }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <div className="relative mt-1">
        <select
          value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          className="w-full h-9 pl-2.5 pr-8 text-sm bg-card border border-border rounded-sm appearance-none focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
        >
          <option value="">All</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      </div>
    </div>
  );
}

export default function Explorer() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const { ids, toggle } = useCompare();

  const [search, setSearch] = useState(params.get("search") || "");
  const [f, setF] = useState({ sector: "", chain: "", stage: "", jurisdiction: "", category: "", status: "", decentralization: "", team_visibility: "", risk_level: "", kyc: "", audit: "" });
  const [minScore, setMinScore] = useState("");
  const [sort, setSort] = useState("score");
  const [order, setOrder] = useState("desc");
  const [cols, setCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem("explorer_cols")) || DEFAULT_COLS; } catch { return DEFAULT_COLS; }
  });
  const [showFilters, setShowFilters] = useState(true);
  const [views, setViews] = useState(() => { try { return JSON.parse(localStorage.getItem("explorer_views")) || []; } catch { return []; } });

  useEffect(() => { api.get("/projects/meta/filters").then((r) => setMeta(r.data)); }, []);
  useEffect(() => { localStorage.setItem("explorer_cols", JSON.stringify(cols)); }, [cols]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const q = { sort, order };
    if (search) q.search = search;
    if (minScore) q.min_score = minScore;
    Object.entries(f).forEach(([k, v]) => { if (v) q[k] = v; });
    try {
      const { data } = await api.get("/projects", { params: q });
      setRows(data.projects);
      setTotal(data.total);
    } finally { setLoading(false); }
  }, [search, minScore, f, sort, order]);

  useEffect(() => { const t = setTimeout(fetchRows, 250); return () => clearTimeout(t); }, [fetchRows]);

  const setSortKey = (key) => {
    if (sort === key) setOrder(order === "desc" ? "asc" : "desc");
    else { setSort(key); setOrder("desc"); }
  };

  const activeFilterCount = Object.values(f).filter(Boolean).length + (minScore ? 1 : 0);
  const clearFilters = () => { setF({ sector: "", chain: "", stage: "", jurisdiction: "", category: "", status: "", decentralization: "", team_visibility: "", risk_level: "", kyc: "", audit: "" }); setMinScore(""); setSearch(""); };

  const saveView = () => {
    const name = prompt("Save current view as:");
    if (!name) return;
    const v = { name, f, search, minScore, sort, order, cols };
    const next = [...views.filter((x) => x.name !== name), v];
    setViews(next); localStorage.setItem("explorer_views", JSON.stringify(next));
    toast.success(`View "${name}" saved`);
  };
  const applyView = (v) => { setF(v.f); setSearch(v.search); setMinScore(v.minScore); setSort(v.sort); setOrder(v.order); setCols(v.cols); };

  const visibleCols = useMemo(() => ALL_COLUMNS.filter((c) => cols.includes(c.key)), [cols]);

  const cell = (p, key) => {
    switch (key) {
      case "score": return <div className="w-28"><ScoreBar score={p.score} /></div>;
      case "category": return <Tag>{p.category}</Tag>;
      case "sectors": return <div className="flex flex-wrap gap-1">{(p.sectors || []).slice(0, 2).map((s) => <Tag key={s}>{s}</Tag>)}</div>;
      case "chains": return <span className="text-xs num">{(p.chains || []).join(", ")}</span>;
      case "stage": return <Tag>{p.stage}</Tag>;
      case "jurisdiction": return <span className="text-xs">{p.jurisdiction}</span>;
      case "fdv": return <span className="num text-xs">{fmtUSD(p.fdv)}</span>;
      case "funds_raised": return <span className="num text-xs text-emerald-700 font-semibold">{fmtUSD(p.funds_raised)}</span>;
      case "tge_date": return <span className="text-xs text-muted-foreground">{fmtDate(p.tge_date)}</span>;
      case "decentralization": return <span className="text-[11px]">{p.decentralization}</span>;
      case "team_visibility": return <span className="text-xs">{p.team_visibility}</span>;
      case "risk_level": return <RiskBadge level={p.risk_level} count={p.red_flag_count || undefined} />;
      default: return null;
    }
  };

  const numericRight = new Set(["fdv", "funds_raised", "score"]);

  return (
    <div className="space-y-4 fade-up" data-testid="explorer-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <Overline>Market Coverage</Overline>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Research Explorer</h1>
        </div>
        <div className="text-xs text-muted-foreground num">{total} projects</div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, symbol, description…" data-testid="explorer-search"
            className="w-full h-9 pl-9 pr-3 text-sm bg-card border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-ring" />
        </div>
        <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters" className={cn("inline-flex items-center gap-2 h-9 px-3 text-sm font-medium border rounded-sm transition-colors", activeFilterCount ? "border-primary bg-accent text-foreground" : "border-border bg-card hover:bg-muted")}>
          <SlidersHorizontal className="w-4 h-4" /> Filters {activeFilterCount > 0 && <span className="num text-xs bg-primary text-primary-foreground rounded-sm px-1.5">{activeFilterCount}</span>}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="columns-btn" className="inline-flex items-center gap-2 h-9 px-3 text-sm font-medium border border-border bg-card hover:bg-muted rounded-sm transition-colors">
              <Columns3 className="w-4 h-4" /> Columns
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {ALL_COLUMNS.map((c) => (
              <DropdownMenuCheckboxItem key={c.key} checked={cols.includes(c.key)} onCheckedChange={(v) => setCols((prev) => v ? [...prev, c.key] : prev.filter((x) => x !== c.key))}>
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="views-btn" className="inline-flex items-center gap-2 h-9 px-3 text-sm font-medium border border-border bg-card hover:bg-muted rounded-sm transition-colors">
              <Save className="w-4 h-4" /> Views
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Saved views</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {views.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">No saved views yet</div>}
            {views.map((v) => (
              <button key={v.name} onClick={() => applyView(v)} className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded-sm">{v.name}</button>
            ))}
            <DropdownMenuSeparator />
            <button onClick={saveView} className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded-sm inline-flex items-center gap-2"><Save className="w-3.5 h-3.5" /> Save current view</button>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters panel */}
      {showFilters && meta && (
        <div className="bg-card border border-border/60 p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="filters-panel">
          <FilterSelect label="Sector" value={f.sector} options={meta.sectors} onChange={(v) => setF({ ...f, sector: v })} testId="filter-sector" />
          <FilterSelect label="Chain" value={f.chain} options={meta.chains} onChange={(v) => setF({ ...f, chain: v })} testId="filter-chain" />
          <FilterSelect label="Launch Stage" value={f.stage} options={meta.stages} onChange={(v) => setF({ ...f, stage: v })} testId="filter-stage" />
          <FilterSelect label="Jurisdiction" value={f.jurisdiction} options={meta.jurisdictions} onChange={(v) => setF({ ...f, jurisdiction: v })} testId="filter-jurisdiction" />
          <FilterSelect label="Category" value={f.category} options={meta.categories} onChange={(v) => setF({ ...f, category: v })} testId="filter-category" />
          <FilterSelect label="Decentralization" value={f.decentralization} options={meta.decentralization} onChange={(v) => setF({ ...f, decentralization: v })} testId="filter-dec" />
          <FilterSelect label="Team Visibility" value={f.team_visibility} options={meta.team_visibility} onChange={(v) => setF({ ...f, team_visibility: v })} testId="filter-team" />
          <FilterSelect label="Risk Level" value={f.risk_level} options={meta.risk_levels} onChange={(v) => setF({ ...f, risk_level: v })} testId="filter-risk" />
          <FilterSelect label="KYC" value={f.kyc} options={["true", "false"]} onChange={(v) => setF({ ...f, kyc: v })} testId="filter-kyc" />
          <FilterSelect label="Audited" value={f.audit} options={["true", "false"]} onChange={(v) => setF({ ...f, audit: v })} testId="filter-audit" />
          <div>
            <label className="overline text-muted-foreground">Min Score</label>
            <input type="number" min="0" max="100" value={minScore} onChange={(e) => setMinScore(e.target.value)} data-testid="filter-minscore"
              className="mt-1 w-full h-9 px-2.5 text-sm bg-card border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-ring" />
          </div>
          <div className="flex items-end">
            <button onClick={clearFilters} data-testid="clear-filters" className="inline-flex items-center gap-1.5 h-9 px-3 text-sm text-muted-foreground hover:text-destructive border border-border rounded-sm hover:border-destructive/40 transition-colors w-full justify-center">
              <X className="w-4 h-4" /> Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-8 px-3 py-2.5"></th>
                <th className="text-left px-3 py-2.5">
                  <button onClick={() => setSortKey("name")} className="inline-flex items-center gap-1 overline text-muted-foreground hover:text-foreground">Project <ArrowUpDown className="w-3 h-3" /></button>
                </th>
                {visibleCols.map((c) => (
                  <th key={c.key} className={cn("px-3 py-2.5", numericRight.has(c.key) ? "text-right" : "text-left")}>
                    <button onClick={() => setSortKey(c.key === "risk_level" ? "red_flag_count" : c.key)} className="inline-flex items-center gap-1 overline text-muted-foreground hover:text-foreground">
                      {c.label} <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleCols.length + 2} className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={visibleCols.length + 2}><EmptyState title="No projects match your filters" sub="Try clearing some filters." icon={Table2} /></td></tr>
              ) : rows.map((p) => {
                const selected = ids.includes(p.id);
                return (
                  <tr key={p.id} onClick={() => nav(`/project/${p.id}`)} className="border-b border-border/40 hover:bg-muted/40 transition-colors group cursor-pointer" data-testid={`row-${p.id}`}>
                    <td className="px-3 py-2.5">
                      <button onClick={(e) => { e.stopPropagation(); toggle(p.id); }} data-testid={`compare-toggle-${p.id}`}
                        className={cn("w-4 h-4 rounded-sm border flex items-center justify-center transition-colors", selected ? "bg-primary border-primary" : "border-border hover:border-primary")}>
                        {selected && <Check className="w-3 h-3 text-primary-foreground" />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <Monogram symbol={p.token_symbol} name={p.name} size={30} />
                        <div className="min-w-0">
                          <div className="font-semibold truncate group-hover:text-primary transition-colors">{p.name}</div>
                          <div className="text-[11px] text-muted-foreground num">{p.token_symbol}</div>
                        </div>
                      </div>
                    </td>
                    {visibleCols.map((c) => (
                      <td key={c.key} className={cn("px-3 py-2.5", numericRight.has(c.key) ? "text-right" : "text-left")}>{cell(p, c.key)}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
